import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Square } from "lucide-react";
import { api } from "@/lib/api";
import { DROP_GRAD } from "@/lib/drops";

const fmt = (s) => new Date(s).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const inp = "w-full h-10 px-3 rounded-xl border border-pink-100 bg-white text-sm";

function Settings({ cfg, onSaved }) {
  const [f, setF] = useState(cfg);
  useEffect(() => setF(cfg), [cfg]);
  const save = async () => {
    try { onSaved((await api.put("/admin/drops/settings", f)).data); toast.success("Gespeichert"); } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Fehler"); }
  };
  return (
    <div className="lux-card rounded-3xl p-5 space-y-3" data-testid="admin-drops-settings">
      <p className="font-bold">Automatische Drops</p>
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.auto} onChange={(e) => setF({ ...f, auto: e.target.checked })} data-testid="admin-drops-auto" /> Automatisch zu zufälligen Zeiten (10–22 Uhr)</label>
      <div className="grid grid-cols-3 gap-2 text-xs font-bold text-slate-500">
        <label>Pro Woche<input type="number" min={0} max={7} className={inp} value={f.per_week} onChange={(e) => setF({ ...f, per_week: +e.target.value })} data-testid="admin-drops-per-week" /></label>
        <label>Punkte<input type="number" min={0} max={1000} className={inp} value={f.points} onChange={(e) => setF({ ...f, points: +e.target.value })} data-testid="admin-drops-points" /></label>
        <label>Minuten<input type="number" min={5} max={240} className={inp} value={f.minutes} onChange={(e) => setF({ ...f, minutes: +e.target.value })} data-testid="admin-drops-minutes" /></label>
      </div>
      <button onClick={save} data-testid="admin-drops-save" className="tap h-10 px-5 rounded-full pink-gradient text-white text-sm font-bold">Speichern</button>
    </div>
  );
}

function StartDrop({ onDone, running }) {
  const [f, setF] = useState({ name: "", emoji: "🎁", color: "rose", points: 25, minutes: 15 });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/admin/drops", f); toast.success("Drop gestartet!"); setF({ ...f, name: "" }); onDone(); } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Bitte Namen (mind. 2 Zeichen) und Emoji angeben"); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card rounded-3xl p-5 space-y-3" data-testid="admin-drops-start">
      <p className="font-bold">Drop jetzt starten</p>
      <div className="grid grid-cols-[64px_1fr] gap-2">
        <input className={`${inp} text-center text-xl`} maxLength={8} value={f.emoji} onChange={(e) => setF({ ...f, emoji: e.target.value })} data-testid="admin-drop-emoji" aria-label="Emoji" />
        <input className={inp} maxLength={40} placeholder="Name des Abzeichens, z. B. Halloween-Geist 2026" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-drop-name" />
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(DROP_GRAD).map(([c, g]) => (
          <button key={c} type="button" onClick={() => setF({ ...f, color: c })} data-testid={`admin-drop-color-${c}`} aria-label={c} className={`w-8 h-8 rounded-full bg-gradient-to-br ${g} ${f.color === c ? "ring-4 ring-rose-200 scale-110" : ""} transition-transform`} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs font-bold text-slate-500">
        <label>Punkte<input type="number" min={0} max={1000} className={inp} value={f.points} onChange={(e) => setF({ ...f, points: +e.target.value })} data-testid="admin-drop-points" /></label>
        <label>Minuten<input type="number" min={5} max={240} className={inp} value={f.minutes} onChange={(e) => setF({ ...f, minutes: +e.target.value })} data-testid="admin-drop-minutes" /></label>
      </div>
      <button onClick={go} disabled={busy || running} data-testid="admin-drop-go" className="tap h-11 px-5 rounded-full pink-gradient text-white text-sm font-extrabold inline-flex items-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} {running ? "Es läuft schon ein Drop" : "Drop starten"}
      </button>
    </div>
  );
}

export function DropsAdmin() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/drops").then(({ data }) => setD(data)).catch(() => toast.error("Konnte Drops nicht laden"));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-rose-500" />;
  const running = d.drops.find((x) => x.active);
  const stop = async (id) => { await api.post(`/admin/drops/${id}/stop`); load(); };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-drops">
      <p className="text-sm text-slate-600">Drops erscheinen nur in der App (Feed + kleine Blase unten links). Wer in der Zeit vorbeischaut, schnappt sich ein limitiertes Abzeichen und Bonuspunkte. Wer ihn verpasst, verliert nichts.</p>
      <Settings cfg={d.settings} onSaved={setD} />
      {d.settings.auto && (
        <p className="text-xs text-slate-500" data-testid="admin-drops-upcoming">Nächste automatische Drops diese Woche (nur für Admins sichtbar): {d.upcoming.length ? d.upcoming.map(fmt).join(" · ") : "keine mehr"}</p>
      )}
      <StartDrop onDone={load} running={!!running} />
      <div className="lux-card rounded-3xl p-5 space-y-2" data-testid="admin-drops-list">
        <p className="font-bold">Letzte Drops</p>
        {!d.drops.length && <p className="text-sm text-slate-500">Noch keine Drops.</p>}
        {d.drops.map((x) => (
          <div key={x.id} className="flex items-center gap-3 py-2 border-b border-pink-50 last:border-0" data-testid={`admin-drop-row-${x.id}`}>
            <span className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${DROP_GRAD[x.color] || DROP_GRAD.rose} flex items-center justify-center text-xl`}>{x.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{x.name}</p>
              <p className="text-xs text-slate-500">{fmt(x.starts_at)} · {x.source === "auto" ? "automatisch" : "von Admin"} · {x.points} Punkte</p>
            </div>
            <span className="text-sm font-extrabold text-rose-600 tabular-nums" data-testid={`admin-drop-claims-${x.id}`}>{x.claims} geschnappt</span>
            {x.active && <button onClick={() => stop(x.id)} data-testid={`admin-drop-stop-${x.id}`} className="h-8 px-3 rounded-full bg-slate-100 text-xs font-bold inline-flex items-center gap-1"><Square className="w-3 h-3" /> Stoppen</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
